import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import {
  CheckCircle2, XCircle, Info, ShieldCheck, Edit3, RotateCcw,
  History, AlertTriangle, Loader2, Award, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const LEVEL_LABELS = {
  level_1_basic: 'Level C — Basic',
  level_2_commitment: 'Level B — Commitment',
  level_3_excellence: 'Level A — Excellence',
};

const ScoreBreakdownModal = ({ open, onClose, entityType, entityId, entityName, isAdmin, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // { key, current, reason, value }
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState('breakdown');
  const [recertifyOpen, setRecertifyOpen] = useState(false);
  const [recertifyNotes, setRecertifyNotes] = useState('');
  const [recertifying, setRecertifying] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (open && entityId && entityType) fetchBreakdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entityId, entityType]);

  const fetchBreakdown = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/certification-renewal/breakdown/${entityType}/${entityId}`,
        { headers }
      );
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load breakdown');
    } finally {
      setLoading(false);
    }
  };

  const submitOverride = async (clear = false) => {
    if (!editing) return;
    const reason = (editing.reason || '').trim();
    if (!reason) {
      toast.error('Reason is required for the audit trail');
      return;
    }
    let newScore = null;
    if (!clear) {
      const parsed = parseFloat(editing.value);
      if (isNaN(parsed) || parsed < 0 || parsed > 100) {
        toast.error('Score must be a number between 0 and 100');
        return;
      }
      newScore = parsed;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/certification-renewal/override/${entityType}/${entityId}`,
        { category: editing.key, new_score: newScore, reason },
        { headers }
      );
      toast.success(clear ? 'Override cleared' : 'Score override applied');
      setEditing(null);
      await fetchBreakdown();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply override');
    } finally {
      setSubmitting(false);
    }
  };

  const submitRecertify = async () => {
    setRecertifying(true);
    try {
      const res = await axios.post(
        `${API}/api/certification-renewal/recertify/${entityType}/${entityId}`,
        { notes: recertifyNotes.trim() },
        { headers }
      );
      toast.success(`Re-certified as ${res.data.new_level_label}`);
      setRecertifyOpen(false);
      setRecertifyNotes('');
      await fetchBreakdown();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to re-certify');
    } finally {
      setRecertifying(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  const breakdown = data?.breakdown;
  const categories = breakdown?.categories ? Object.entries(breakdown.categories) : [];
  const hasOverrides = breakdown?.has_overrides;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="score-breakdown-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
            Score Breakdown — {entityName}
          </DialogTitle>
        </DialogHeader>

        {loading || !data ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="mt-2">
            <TabsList>
              <TabsTrigger value="breakdown" data-testid="tab-breakdown">Breakdown</TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">
                <History className="h-3 w-3 mr-1" /> Audit Trail ({data.history?.length || 0})
              </TabsTrigger>
            </TabsList>

            {/* ─── BREAKDOWN TAB ─── */}
            <TabsContent value="breakdown" className="space-y-4 pt-4">
              {/* Overall summary */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-4 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-xs text-blue-700 font-medium">Overall Score</p>
                  <div className="flex items-baseline gap-3 mt-1">
                    <span className="text-3xl font-bold text-blue-900">{Math.round(breakdown.effective_overall ?? breakdown.overall ?? 0)}%</span>
                    {hasOverrides && (
                      <span className="text-sm text-gray-500 line-through">{Math.round(breakdown.overall ?? 0)}% raw</span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-blue-700 font-medium">Current Level</p>
                  <Badge className="bg-gray-700 text-white mt-1">
                    {data.current_level_label || 'Not Certified'}
                  </Badge>
                  {data.level_changed && (
                    <div className="mt-2 flex items-center gap-2 justify-end">
                      <ArrowRight className="h-3 w-3 text-amber-600" />
                      <Badge className="bg-blue-600 text-white">
                        Recommended: {data.recommended_level_label}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>

              {/* Re-certify CTA — only when admin AND recommended differs from current */}
              {isAdmin && data.level_changed && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-testid="recertify-cta">
                  <Award className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900">
                      Recommended level differs from current certification
                    </p>
                    <p className="text-xs text-blue-800 mt-1">
                      Based on the effective overall score of <strong>{Math.round(breakdown.effective_overall ?? 0)}%</strong>
                      {hasOverrides ? ' (with admin overrides applied)' : ''}, this entity should be at
                      &nbsp;<strong>{data.recommended_level_label}</strong>. Re-certifying promotes/demotes the certification
                      level, refreshes the renewal clock, and writes an audit entry.
                    </p>
                  </div>
                  <Button
                    onClick={() => setRecertifyOpen(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="recertify-btn"
                  >
                    <Award className="h-4 w-4 mr-2" />
                    Re-certify as {data.recommended_level_label}
                  </Button>
                </div>
              )}

              {hasOverrides && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    Some category scores have been <strong>manually overridden by an administrator</strong>.
                    Each override is recorded in the audit trail with the responsible user and the justification.
                  </div>
                </div>
              )}

              {/* Categories */}
              {categories.map(([key, cat]) => {
                const overridden = cat.override !== null && cat.override !== undefined;
                const display = cat.effective_score;
                return (
                  <div key={key} className="border rounded-lg p-4 bg-white" data-testid={`category-${key}`}>
                    <div className="flex items-start justify-between mb-3 gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold text-gray-900">{cat.label}</h4>
                          <Badge variant="outline" className="text-xs">Weight: {cat.weight}%</Badge>
                          {overridden && (
                            <Badge className="bg-amber-100 text-amber-800 text-xs">
                              <Edit3 className="h-3 w-3 mr-1" /> Overridden
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {cat.achieved} of {cat.max} indicators met
                        </p>
                      </div>
                      <div className="text-right flex flex-col items-end gap-1">
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-bold text-gray-900">{Math.round(display)}%</span>
                          {overridden && (
                            <span className="text-xs text-gray-500 line-through">{Math.round(cat.score)}%</span>
                          )}
                        </div>
                        {isAdmin && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => setEditing({ key, label: cat.label, current: display, value: String(Math.round(display)), reason: '' })}
                            data-testid={`override-btn-${key}`}
                          >
                            <Edit3 className="h-3 w-3 mr-1" /> Override
                          </Button>
                        )}
                      </div>
                    </div>

                    <Progress value={display} className="h-2 mb-3" />

                    {/* Indicators */}
                    <div className="space-y-1.5">
                      {cat.indicators.map((ind) => (
                        <div key={ind.key} className="flex items-start gap-2 text-sm">
                          {ind.achieved ? (
                            <CheckCircle2 className="h-4 w-4 text-green-700 flex-shrink-0 mt-0.5" />
                          ) : (
                            <XCircle className="h-4 w-4 text-gray-300 flex-shrink-0 mt-0.5" />
                          )}
                          <div className="flex-1">
                            <span className={ind.achieved ? 'text-gray-900' : 'text-gray-500'}>
                              {ind.label}
                            </span>
                            {ind.value !== null && ind.value !== undefined && ind.value !== '' && (
                              <span className="ml-2 text-xs text-gray-500">
                                ({typeof ind.value === 'boolean' ? (ind.value ? 'yes' : 'no') : String(ind.value).slice(0, 80)})
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              <div className="text-xs text-gray-500 bg-gray-50 rounded-md p-3 flex gap-2">
                <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  Scores are auto-calculated from the organisation's registration data. Each indicator
                  contributes one point to its category; the category percentages are then weighted into
                  the overall score. Certification levels: ≥80% Level A, 60–79% Level B, 40–59% Level C.
                </span>
              </div>
            </TabsContent>

            {/* ─── AUDIT TRAIL TAB ─── */}
            <TabsContent value="history" className="pt-4">
              {(data.history || []).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <History className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p>No score overrides recorded for this entity yet.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {data.history.map((h) => (
                    <div key={h.id} className="border rounded-lg p-3 bg-white" data-testid={`audit-${h.id}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="capitalize text-xs">{h.category.replace(/_/g, ' ')}</Badge>
                          <Badge className={
                            h.action === 'cleared' ? 'bg-gray-100 text-gray-700' :
                            h.action === 'created' ? 'bg-blue-100 text-blue-700' :
                            'bg-amber-100 text-amber-800'
                          }>{h.action}</Badge>
                        </div>
                        <span className="text-xs text-gray-500">{formatDate(h.created_at)}</span>
                      </div>
                      <div className="text-sm text-gray-700 mt-1">
                        <span className="text-gray-500">Score: </span>
                        <span className="font-medium">
                          {h.old_value === null || h.old_value === undefined ? `${Math.round(h.calculated_score ?? 0)}% (calculated)` : `${Math.round(h.old_value)}%`}
                        </span>
                        <span className="mx-2 text-gray-500">→</span>
                        <span className="font-semibold">
                          {h.new_value === null || h.new_value === undefined ? `${Math.round(h.calculated_score ?? 0)}% (calculated)` : `${Math.round(h.new_value)}%`}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        <span className="text-gray-500">By:</span> <strong>{h.overridden_by_name}</strong>
                      </div>
                      <div className="text-sm text-gray-700 mt-1 italic">"{h.reason}"</div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* ─── Override dialog ─── */}
        {editing && (
          <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
            <DialogContent className="max-w-md" data-testid="override-dialog">
              <DialogHeader>
                <DialogTitle>Override "{editing.label}" Score</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900">
                  <p>
                    The current effective score is <strong>{Math.round(editing.current)}%</strong>.
                    Any change here will be recorded in the audit trail with your username.
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    New score (0–100)
                  </label>
                  <Input
                    type="number" min="0" max="100" step="1"
                    value={editing.value}
                    onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                    data-testid="override-score-input"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    Reason for override <span className="text-red-600">*</span>
                  </label>
                  <Textarea
                    rows={3}
                    placeholder="e.g. Verified additional governance evidence not captured in the registration form (board minutes 2025)."
                    value={editing.reason}
                    onChange={(e) => setEditing({ ...editing, reason: e.target.value })}
                    data-testid="override-reason-input"
                  />
                </div>

                <div className="flex justify-between gap-2">
                  <Button
                    variant="outline"
                    onClick={() => submitOverride(true)}
                    disabled={submitting}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="override-clear-btn"
                  >
                    <RotateCcw className="h-3 w-3 mr-1" /> Clear override
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setEditing(null)} disabled={submitting}>Cancel</Button>
                    <Button
                      onClick={() => submitOverride(false)}
                      disabled={submitting}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="override-save-btn"
                    >
                      {submitting && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                      Save override
                    </Button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
        {/* ─── Re-certify dialog ─── */}
        <Dialog open={recertifyOpen} onOpenChange={(v) => !v && setRecertifyOpen(false)}>
          <DialogContent className="max-w-md" data-testid="recertify-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Award className="h-5 w-5 text-blue-600" /> Confirm Re-certification
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-gray-700 text-white">{data?.current_level_label || 'Not Certified'}</Badge>
                  <ArrowRight className="h-4 w-4 text-blue-600" />
                  <Badge className="bg-blue-600 text-white">{data?.recommended_level_label || 'Not Certified'}</Badge>
                </div>
                <p className="mt-2 text-gray-700">
                  This action will update the certification level for <strong>{entityName}</strong>,
                  reset the certification date to today, and append an entry to the audit trail.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Notes (optional)
                </label>
                <Textarea
                  rows={3}
                  placeholder="e.g. Promoted after manual review of governance evidence and updated 2026 audit report."
                  value={recertifyNotes}
                  onChange={(e) => setRecertifyNotes(e.target.value)}
                  data-testid="recertify-notes"
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setRecertifyOpen(false)} disabled={recertifying}>
                  Cancel
                </Button>
                <Button
                  onClick={submitRecertify}
                  disabled={recertifying}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="recertify-confirm-btn"
                >
                  {recertifying && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                  Confirm Re-certify
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default ScoreBreakdownModal;
